import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CommonTable from "../../components/commonComponent/CommanTable";
import axiosInstance from "../../utils/axiosInstance";
import { toastSuccess, toastError } from "../../utils/alertHelper";

// Table columns for variants
const variantColumns = [
  { key: "srNo", label: "#" },
  { key: "name", label: "Product Name" },
  { key: "companyName", label: "Brand/Company" },
  { key: "unit", label: "Unit" },
  { key: "hsn", label: "HSN/SAC" },
  { key: "rate", label: "Rate (₹)" },
  { key: "gst", label: "GST %" },
  { key: "quantity", label: "Qty" },
  { key: "total", label: "Total (₹)" },
  { key: "description", label: "Description" },
  { key: "isAvailable", label: "Stock" }
];

const IMAGE_BASE_URL = process.env.REACT_APP_Image_URL || "https://api.solarestimate.in/image/";

export default function OrderView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("items");
  const [showTimeline, setShowTimeline] = useState(false);

  // Fetch order data from API
  const fetchOrder = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(`/admin/api/order/${id}`);
      const data = response?.data || response;
      
      if (data && (data.Status === 1 || data.status === 1) && data.order) {
        setOrder(data.order);
        toastSuccess(data.Message || 'Order fetched successfully');
      } else {
        const errorMessage = data.Message || data.message || "Failed to fetch order";
        setError(errorMessage);
        toastError(errorMessage);
      }
    } catch (err) {
      console.error("Error fetching order:", err);
      const errorMessage = err.response?.data?.Message || 
                          err.response?.data?.message || 
                          err.message || 
                          "Failed to fetch order. Please try again.";
      setError(errorMessage);
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchOrder();
  }, [id]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) setUser(JSON.parse(stored));
    } catch (err) {
      // ignore
    }
  }, []);

  const baseRoute = (user && user.userType === 'Client') ? '/user' : '/admin';

  // Calculate order totals
  const calculateTotals = () => {
    const items = order?.items || [];
    
    const subtotal = items.reduce((sum, entry) => {
      const price = entry.price != null ? entry.price : entry.rate || 0;
      const qty = entry.quantity || entry.qty || 0;
      return sum + (price * qty);
    }, 0);
    
    const totalTax = items.reduce((sum, entry) => {
      const price = entry.price != null ? entry.price : entry.rate || 0;
      const qty = entry.quantity || entry.qty || 0;
      const taxRate = entry.gstRate || entry.gst || 0;
      return sum + ((price * qty * taxRate) / 100);
    }, 0);
    
    const discount = order?.discount || 0;
    const adjustments = order?.adjustments || 0;
    const grandTotal = subtotal + totalTax - discount + adjustments;
    
    return { subtotal, totalTax, discount, adjustments, grandTotal };
  };

  // Prepare order item rows for table
  const itemRows = (order?.items || []).map((entry, index) => {
    const it = entry.item || {};
    const price = entry.price != null ? entry.price : entry.rate || 0;
    const qty = entry.quantity || entry.qty || 0;
    const total = price * qty;
    const taxRate = entry.gstRate || entry.gst || 0;
    const taxAmount = (price * qty * taxRate) / 100;
    
    return {
      ...entry,
      srNo: index + 1,
      name: it.name || it.itemName || "Unnamed Product",
      companyName: (it.group && (it.group.name || it.group)) || "—",
      unit: it.unit?.name || it.unit || "—",
      hsn: it.hsn || it.hsnCode || "—",
      rate: price.toFixed(2),
      gst: `${taxRate}%`,
      quantity: qty,
      total: total.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      description: it.description || entry.description || "—",
      isAvailable: (
        <span className={`badge ${it.isAvailable ? "bg-success" : "bg-secondary"} px-3 py-2 rounded-pill`}>
          <i className={`fas fa-${it.isAvailable ? "check-circle" : "times-circle"} me-1`}></i>
          {it.isAvailable ? "In Stock" : "Out of Stock"}
        </span>
      )
    };
  }) || [];

  const filteredRows = itemRows.filter(row =>
    Object.values(row).join(" ").toString().toLowerCase().includes(search.toLowerCase())
  );

  const totals = calculateTotals();

  // Get status badge color
  const getStatusBadge = (status) => {
    const statusColors = {
      'New': 'bg-info',
      'Confirmed': 'bg-primary',
      'On Hold': 'bg-warning',
      'Partially Dispatched': 'bg-warning',
      'Fully Dispatched': 'bg-success',
      'Cancelled': 'bg-danger'
    };
    return statusColors[status] || 'bg-secondary';
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted fs-5">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger border-0 shadow-sm rounded-4 p-4" role="alert">
          <div className="d-flex align-items-center mb-3">
            <div className="bg-danger bg-opacity-10 p-3 rounded-circle me-3">
              <i className="fas fa-exclamation-triangle fs-4 text-danger"></i>
            </div>
            <div>
              <h4 className="text-danger mb-1">Error Loading Order</h4>
              <p className="mb-0 text-secondary">{error}</p>
            </div>
          </div>
        </div>
        <button 
          className="btn btn-primary rounded-pill px-4"
          onClick={() => navigate('/admin/order')}
        >
          <i className="fas fa-arrow-left me-2"></i> Back to Orders
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container py-5">
        <div className="text-center py-5">
          <div className="mb-4">
            <div className="bg-light p-4 d-inline-block rounded-circle">
              <i className="fas fa-inbox fa-4x text-muted"></i>
            </div>
          </div>
          <h4 className="text-muted mb-3">Order Not Found</h4>
          <p className="text-secondary mb-4">The order you're looking for doesn't exist or has been removed.</p>
          <button 
            className="btn btn-primary rounded-pill px-4"
            onClick={() => navigate('/admin/order')}
          >
            <i className="fas fa-arrow-left me-2"></i> Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 py-4" style={{ maxWidth: 1400 }}>
      {/* Header with breadcrumb */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-1">
              <li className="breadcrumb-item">
                  <a href="/admin/dashboard" className="text-decoration-none" onClick={(e) => { e.preventDefault(); navigate(`${baseRoute}/dashboard`); }}>
                  Dashboard
                </a>
              </li>
              <li className="breadcrumb-item">
                <a href="/admin/order" className="text-decoration-none" onClick={(e) => { e.preventDefault(); navigate(`${baseRoute}/order`); }}>
                  Orders
                </a>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {order.code || order.orderid || `Order #${order.id}`}
              </li>
            </ol>
          </nav>
          <div className="d-flex align-items-center gap-3 mt-2">
            <h1 className="fw-bold mb-0" style={{ fontSize: "1.75rem" }}>
              {order.code || order.orderid || `Order #${order.id}`}
            </h1>
            <span className={`badge ${getStatusBadge(order.status)} px-4 py-2 rounded-pill fs-6`}>
              {order.status || 'Draft'}
            </span>
          </div>
        </div>
        <div className="d-flex gap-2 mt-3 mt-md-0">
          <button 
            className="btn btn-outline-primary rounded-pill px-3"
            onClick={() => navigate(`${baseRoute}/order/edit/${id}`)}
          >
            <i className="fas fa-edit me-2"></i> Edit Order
          </button>
          <button 
            className="btn btn-outline-secondary rounded-pill px-3"
            onClick={() => window.print()}
          >
            <i className="fas fa-print me-2"></i> Print
          </button>
          <a 
            href="/admin/order" 
            className="btn btn-link text-decoration-none"
            onClick={(e) => { e.preventDefault(); navigate('/admin/order'); }}
          >
            <i className="fas fa-times me-1"></i> Close
          </a>
        </div>
      </div>

      {/* Order Overview Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-8">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-4">
              <h5 className="card-title fw-semibold mb-3 d-flex align-items-center">
                <i className="fas fa-info-circle me-2 text-primary"></i>
                Order Information
              </h5>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Order Date</small>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-calendar-alt me-2 text-secondary"></i>
                      <span className="fw-medium">{formatDate(order.date || order.orderdate)}</span>
                    </div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Expected Delivery</small>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-truck me-2 text-secondary"></i>
                      <span className="fw-medium">{formatDate(order.expectedDate)}</span>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">E-Way Bill</small>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-file-invoice me-2 text-secondary"></i>
                      <span className="fw-medium">{order.eway || order.ewayBill || order.ewaybill || '—'}</span>
                    </div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Transport Details</small>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-truck-moving me-2 text-secondary"></i>
                      <span className="fw-medium">
                        {order.transportNo || order.transportNumber || '—'}
                        {order.lrNo || order.lrNumber ? ` (LR: ${order.lrNo || order.lrNumber})` : ''}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card border-0 shadow-sm rounded-4 h-100 bg-primary bg-opacity-10">
            <div className="card-body p-4">
              <h5 className="card-title fw-semibold mb-3 d-flex align-items-center">
                <i className="fas fa-user me-2 text-primary"></i>
                Customer Details
              </h5>
              <div className="mb-2">
                <span className="fw-bold fs-5">
                  {(order.clientName && ((order.clientName.firstName || '') + (order.clientName.lastName ? ` ${order.clientName.lastName}` : ''))) || 
                   order.customerName || '—'}
                </span>
              </div>
              <div className="d-flex align-items-center text-secondary mb-2">
                <i className="fas fa-phone-alt me-2"></i>
                <span>{order.clientName?.mobileNumber || order.clientName?.mobile || '—'}</span>
              </div>
              {order.clientName?.email && (
                <div className="d-flex align-items-center text-secondary">
                  <i className="fas fa-envelope me-2"></i>
                  <span className="text-truncate">{order.clientName.email}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Address Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-4">
              <h6 className="fw-semibold mb-3 d-flex align-items-center">
                <i className="fas fa-map-marker-alt me-2 text-danger"></i>
                Billing Address
              </h6>
              <p className="mb-0 text-secondary" style={{ lineHeight: 1.6 }}>
                {order.clientBillingAddress ? 
                  (typeof order.clientBillingAddress === 'object' ? 
                    Object.values(order.clientBillingAddress).filter(Boolean).join(', ') : 
                    order.clientBillingAddress) : 
                  (order.clientName?.bill ? 
                    Object.values(order.clientName.bill).filter(Boolean).join(', ') : 
                    '—')}
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-4">
              <h6 className="fw-semibold mb-3 d-flex align-items-center">
                <i className="fas fa-truck me-2 text-success"></i>
                Shipping Address
              </h6>
              <p className="mb-0 text-secondary" style={{ lineHeight: 1.6 }}>
                {order.shipping?.address || 
                 (order.shipping && Object.values(order.shipping).filter(Boolean).join(', ')) || 
                 'Same as Billing Address'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Items Section */}
      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-header bg-transparent border-0 pt-4 px-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <h5 className="fw-semibold mb-0 d-flex align-items-center">
              <i className="fas fa-boxes me-2 text-primary"></i>
              Order Items
              <span className="badge bg-primary bg-opacity-10 text-primary ms-3 px-3 py-2 rounded-pill">
                {(order.items || []).length} {order.items?.length === 1 ? 'Item' : 'Items'}
              </span>
            </h5>
            <div className="mt-2 mt-sm-0" style={{ minWidth: 250 }}>
              <div className="input-group">
                <span className="input-group-text bg-transparent border-end-0">
                  <i className="fas fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0"
                  placeholder="Search items..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <button 
                    className="btn btn-link text-decoration-none"
                    onClick={() => setSearch('')}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="card-body p-4 pt-3">
          {itemRows.length === 0 ? (
            <div className="text-center py-5">
              <div className="bg-light p-4 d-inline-block rounded-circle mb-3">
                <i className="fas fa-box fa-3x text-muted"></i>
              </div>
              <h6 className="text-muted mb-2">No items found</h6>
              <p className="text-secondary small mb-0">
                {search ? 'Try adjusting your search terms' : 'This order has no items'}
              </p>
            </div>
          ) : (
            <CommonTable
              columns={variantColumns}
              data={filteredRows}
              itemsPerPageOptions={[10, 15, 25, 50]}
              defaultItemsPerPage={15}
              bordered={false}
              hover
              striped
              responsive
            />
          )}
        </div>
      </div>

      {/* Order Summary */}
      <div className="row g-3">
        <div className="col-md-6">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-4">
              <h6 className="fw-semibold mb-3 d-flex align-items-center">
                <i className="fas fa-file-alt me-2 text-info"></i>
                Additional Information
              </h6>
              <div className="mb-3">
                <small className="text-muted d-block mb-1">Terms & Conditions</small>
                <p className="text-secondary mb-0 bg-light p-3 rounded-3">
                  {order.termsandCondition || order.terms || '—'}
                </p>
              </div>
              <div>
                <small className="text-muted d-block mb-1">Notes</small>
                <p className="text-secondary mb-0 bg-light p-3 rounded-3">
                  {order.note || order.notes || '—'}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-md-6">
          <div className="card border-0 shadow-sm rounded-4 bg-light bg-gradient">
            <div className="card-body p-4">
              <h6 className="fw-semibold mb-4 d-flex align-items-center">
                <i className="fas fa-calculator me-2 text-primary"></i>
                Order Summary
              </h6>
              
              <div className="d-flex justify-content-between mb-2">
                <span className="text-secondary">Subtotal</span>
                <span className="fw-medium">₹ {totals.subtotal.toFixed(2)}</span>
              </div>
              
              <div className="d-flex justify-content-between mb-2">
                <span className="text-secondary">Tax Total</span>
                <span className="fw-medium">₹ {totals.totalTax.toFixed(2)}</span>
              </div>
              
              {totals.discount > 0 && (
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-secondary">Discount</span>
                  <span className="fw-medium text-success">- ₹ {totals.discount.toFixed(2)}</span>
                </div>
              )}
              
              {totals.adjustments !== 0 && (
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-secondary">Adjustments</span>
                  <span className="fw-medium">₹ {totals.adjustments.toFixed(2)}</span>
                </div>
              )}
              
              <hr className="my-3" />
              
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold fs-5">Grand Total</span>
                <span className="fw-bold fs-4 text-primary">₹ {totals.grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end gap-2 mt-4 pt-2">
        <button 
          className="btn btn-secondary px-4"
          onClick={() => navigate(`${baseRoute}/order`)}
        >
          <i className="fas fa-arrow-left me-2"></i> Back to Orders
        </button>
        <button 
          className="btn btn-primary px-4"
          onClick={() => navigate(`${baseRoute}/order/edit/${id}`)}
        >
          <i className="fas fa-edit me-2"></i> Edit Order
        </button>
      </div>
    </div>
  );
}